import { query, orderBy, where, limit } from 'firebase/firestore';
import { useFirestoreQueryData } from '@react-query-firebase/firestore';
import { FBD_AuditLog, DTAuditLog } from '@rabbit/data/types';
import { useEffect, useState } from 'react';
import { DR_AuditLog } from '@rabbit/data/portal';

interface UseGetAuditLogsProps {
  entityType?: string;
  entityLink?: string;
  tenantLink?: string;
  limitCount?: number;
}

export function useGetAuditLogs({
  entityType,
  entityLink,
  tenantLink,
  limitCount,
}: UseGetAuditLogsProps) {
  const [queryEnabled, setQueryEnabled] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                              Query Constraints                              */
  /* -------------------------------------------------------------------------- */

  const queryConstraints = [];

  if (entityType) {
    queryConstraints.push(where('entityType', '==', entityType));
  }

  if (entityLink) {
    queryConstraints.push(where('entityLink', '==', entityLink));
  }

  if (tenantLink) {
    queryConstraints.push(where('tenantLink', '==', tenantLink));
  }

  // Add limit if provided
  if (limitCount && limitCount > 0) {
    queryConstraints.push(limit(limitCount));
  }

  // Add orderBy for timestamp (most recent first)
  queryConstraints.push(orderBy('timestamp', 'desc'));

  /* ------------------------------ Create query ------------------------------ */
  const auditLogQuery = query(DR_AuditLog.collection(), ...queryConstraints);

  const auditLogList = useFirestoreQueryData<DTAuditLog>(
    ['auditLogs', entityType, entityLink, tenantLink, limitCount],
    auditLogQuery,
    { subscribe: true },
    {
      enabled: queryEnabled,
      refetchOnWindowFocus: true,
      retry: true,
      onError: (err) => {
        console.error('Error fetching audit logs:', err);
      },
    }
  );

  useEffect(() => {
    const canRunQuery = () => {
      // At least one of these must be provided
      if (!entityType && !entityLink && !tenantLink) return false;
      return true;
    };
    setQueryEnabled(canRunQuery());
  }, [entityType, entityLink, tenantLink]);

  return {
    auditLogs: auditLogList.data ?? [],
    isLoading: auditLogList.isLoading || queryEnabled === false,
  };
}
